'use client'

import * as React from 'react'
import {useDebounce} from 'hooks/useDebounce'
import Image from 'next/image'
import searchImg from 'assets/blogs/search.svg'
import classes from './styles.module.css'
import {useSearchParams} from 'next/navigation'

interface DebouncedInputProps {
  onSearch: (text: string) => void
  setSearchedBlogs?: any
  setSearchText?: any
  debounceMs?: number
  immediate?: boolean
  serverSide?: boolean
  preventRedirect?: boolean // Add this new prop
}

export default function DebouncedInput({
  onSearch,
  debounceMs = 2000,
  immediate = false,
  serverSide = false,
  preventRedirect = false, // Default to false for backward compatibility
}: DebouncedInputProps) {
  const searchParams = useSearchParams()

  const initialSearchTerm = serverSide ? searchParams.get('q') || '' : ''
  const [searchTerm, setSearchTerm] = React.useState(initialSearchTerm)
  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)

    if (immediate && !serverSide && value.trim()) {
      onSearch(value)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Only trigger search if there's actual text or preventRedirect is true
    if (searchTerm.trim() || preventRedirect) {
      onSearch(searchTerm)
    }
  }

  React.useEffect(() => {
    // Only trigger auto-search if not immediate and not serverSide
    // Also only if there's actual text or preventRedirect is true
    if (!immediate && !serverSide && (debouncedSearchTerm.trim() || preventRedirect)) {
      onSearch(debouncedSearchTerm)
    }
  }, [debouncedSearchTerm, immediate, onSearch, serverSide, preventRedirect])

  return (
    <form className={classes.search} onSubmit={handleSubmit}>
      <Image src={searchImg} alt="" />
      <input
        type="text"
        placeholder="What are you looking for?"
        value={searchTerm}
        onChange={handleChange}
      />
      <button type="submit" style={{display: 'none'}}></button>
    </form>
  )
}
